import React, { useId, useRef, useState } from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { ReactComponent as UploadFile } from '../../assets/images/upload_consent_file.svg'

type ConsentEvidenceUploadProps = {
  required?: boolean
  onFileChange: (file: File | null) => void
  file: File | null
}

const SUPPORTED_MIME_TYPES = ['application/pdf', 'image/png', 'image/jpeg']

export const ConsentEvidenceUpload: React.FC<ConsentEvidenceUploadProps> = ({ required = false, onFileChange, file }) => {
  const inputId = useId()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isHovering, setIsHovering] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null
    onFileChange(selected)
  }

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsHovering(false)
    const dropped = e.dataTransfer.files?.[0]
    if (dropped && SUPPORTED_MIME_TYPES.includes(dropped.type)) {
      onFileChange(dropped)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsHovering(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsHovering(false)
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault()
    onFileChange(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const labelText = `Document ${required ? '' : '(optional)'}`

  return (
    <div className='d-flex gap-4 align-items-start'>
      <div className='d-flex align-items-center flex-shrink-0'>
        <span className='paragraph-medium zocks-text-gray-900'>{labelText}</span>
      </div>
      <div className='flex-grow-1'>
        <Form.Control ref={fileInputRef} id={inputId} type='file' accept={SUPPORTED_MIME_TYPES.join(',')} onChange={handleChange} hidden />
        {file ? (
          <div className='d-flex align-items-center gap-2 border rounded-2 zocks-p-0.625 zocks-bg-color-primary-light'>
            <i className='bi bi-file-earmark zocks-text-gray-700 fs-5' />
            <span className='paragraph-medium zocks-text-gray-900 flex-grow-1 text-truncate'>{file.name}</span>
            <button type='button' className='btn btn-link p-0 zocks-text-gray-600' onClick={handleRemove} aria-label='Remove file'>
              <i className='bi bi-x-lg' />
            </button>
          </div>
        ) : (
          <label
            htmlFor={inputId}
            className={classNames(
              'd-flex flex-column align-items-center justify-content-center py-5 rounded-3 border zocks-border-dashed zocks-bg-color-gray-100 cursor-pointer consent-evidence-dropzone',
              { 'consent-evidence-dropzone--hovering': isHovering },
            )}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className='mb-2'>
              <UploadFile />
            </div>
            <div className='d-flex flex-column align-items-center gap-1'>
              <span className='paragraph-medium fw-bold zocks-text-gray-900'>Drop your file here or click to browse</span>
              <span className='paragraph-small zocks-text-gray-700'>Supported formats: PDF, PNG, JPG</span>
            </div>
          </label>
        )}
      </div>
    </div>
  )
}
